import { z } from 'zod';
import { PERF_BUCKET_COUNT, PERF_SCHEMA_VERSION } from '../perfHistogram.js';

// ============================================================
// FIELD-TABLET PERFORMANCE — store-and-forward timings (POST /api/field/perf).
//
// A tablet at a site with patchy signal records how long it takes to start, to
// change screen, to open the camera, and how often a frame stalls — whether or
// not it is online — and uploads a summary on the next successful sync, AFTER
// punches and photos. The console reads it so "the Sharjah Camp tablet feels
// slow" becomes a number somebody can look up from a desk.
//
// TIMINGS AND DEVICE FACTS ONLY. No worker name, code, face or punch content
// can be expressed in this shape: metric names and labels are closed
// vocabularies (the enums below), and the one free-ish string — a worst event's
// `d` detail — is restricted to a token alphabet that cannot spell a name, and
// the device additionally reduces it to key=value tokens from a fixed list
// before it is ever stored (apps/field/src/perf/perfVocab.ts).
//
// IDEMPOTENT by `batchKey`: the device freezes a batch before sending it and
// resends the same key until it is answered, so a retry can never double-count.
// ============================================================

/**
 * Every timing the device may report. Names are the `[hadir-perf]` log events
 * (apps/field/src/lib/perfLog.ts and the native `PerfMarks.mark` sites), so a
 * logcat line and a console figure are the same measurement by name.
 * Append only — a name removed here makes older tablets' batches 422.
 */
export const PERF_METRICS = [
  'cold.start', // native: process start -> first usable frame (a COLD start only)
  'boot.webframe', // WebView document start -> first frame (every page load); main.tsx
  'main.block', // native UI thread stalled >= 200 ms during the first 20 s of a process
  'frame.long', // WebView long animation frame >= 100 ms (Chromium 123+); label = screen id
  'task.long', // WebView long task >= 100 ms, where frames cannot be observed; label = screen id
  'screen.paint', // tap -> the new screen's first frame; label = screen id
  'camera.open', // getUserMedia -> stream; label = lens|size
  'camera.reuse', // a parked stream re-attached instead of re-opened
  'capture.oncreate', // native capture Activity: onCreate total
  'capture.inflate', // native capture Activity: layout inflate
  'capture.engine', // native capture Activity: face engine ready
  'capture.provider', // native capture Activity: onCreate -> ProcessCameraProvider in hand
  'capture.bound', // native capture Activity: onCreate -> bindToLifecycle returned
  'capture.frame1', // native capture Activity: onCreate -> first frame off the camera
  'capture.nopreview', // native capture Activity: closed having never had a frame
  'face.warm', // native face engine warm-up at boot
  'camera.warm', // native CameraX warm-up at boot
  'home.boot', // gate home: every first read
  'home.cfg',
  'home.summary',
  'home.roster',
  'home.pending',
  'enrol.encode', // enrolment frame -> JPEG
  'enrol.focus', // enrolment focus request round trip
  'camera.fail', // native camera watchdog: one failed open or a stalled stream; detail reason=… try=…; label = screen
  'warm.start', // native: activity created in a process that already existed -> first usable frame
] as const;
/* `lib/screenPerf.ts` watchLongFrames: exactly one of frame.long / task.long is
   ever recorded by one WebView, and `device.frameSource` says which — the
   console reads the two together as "slow frames". */
export type PerfMetric = (typeof PERF_METRICS)[number];

/** Screen ids (apps/field/src/app/types.ts ScreenId) and camera keys. A label
 *  outside this list is dropped to '' on the device, and again here. */
export const PERF_LABELS = [
  '',
  'setup',
  'signin',
  'start',
  'scanner',
  'people',
  'camp',
  'transfer',
  'dispatch',
  'worker',
  'enroll',
  'enrollConfirm',
  'activity',
  'summary',
  'settings',
  'panel',
  'user|vga',
  'user|hd',
  'environment|vga',
  'environment|hd',
] as const;
export type PerfLabel = (typeof PERF_LABELS)[number];

/** How the device observed long frames. `none` means frame stalls were NOT
 *  measured on that tablet — the console says so rather than printing 0. */
export const PERF_FRAME_SOURCES = ['loaf', 'longtask', 'none'] as const;

/**
 * WHY A TABLET THREW A MEASUREMENT AWAY. Must equal PERF_DROP_REASONS in
 * apps/field/src/perf/perfVocab.ts, where each one is defined against the code
 * path that counts it.
 *
 *   full     the device buffer's own byte cap shed the oldest hours.
 *   storage  the WebView's storage was needed by a record that OUTRANKS a
 *            timing — an enrolment, a roster mark — and telemetry gave its
 *            room up. The tablet behaved correctly; nothing needs clearing.
 *   refused  THIS SERVER answered 413 or 422, so the device gave the batch up
 *            rather than let it block the queue. A fleet-side fault.
 *   late     an event arrived for an hour already uploaded. Events only.
 *
 * The console used to print "its buffer was full" beside every one of these.
 * Only `full` is that sentence, and the three others each want a different
 * thing done about them, so the cause is part of the contract.
 */
export const PERF_DROP_REASONS = ['full', 'storage', 'refused', 'late'] as const;
export type PerfDropReason = (typeof PERF_DROP_REASONS)[number];

/** The detail alphabet: what `key=value` tokens of numbers, resolutions and a
 *  fixed word list can contain. No upper case, so no worker code; no spaces
 *  inside a token, and the device drops any token it does not recognise. */
export const PERF_DETAIL_RE = /^[a-z0-9=@.,/|?_ -]{0,120}$/;

const Ms = z.number().int().min(0).max(600_000);

const DropCount = z.object({
  windows: z.number().int().min(0).max(1_000_000),
  events: z.number().int().min(0).max(1_000_000_000),
});

/** One entry per cause that fired — never a zero, so an absent key reads as
 *  "this did not happen" and an absent `by` as "this build cannot say". The
 *  keys are spelled out rather than built from PERF_DROP_REASONS so the type
 *  is exact; `fieldPerf.schema.test.ts` fails if the two ever diverge. */
export const FieldPerfDropBySchema = z.object({
  full: DropCount.optional(),
  storage: DropCount.optional(),
  refused: DropCount.optional(),
  late: DropCount.optional(),
});
export type FieldPerfDropBy = z.infer<typeof FieldPerfDropBySchema>;

export const FieldPerfDroppedSchema = z.object({
  windows: z.number().int().min(0).max(1_000_000),
  events: z.number().int().min(0).max(1_000_000_000),
  by: FieldPerfDropBySchema.optional(),
});
export type FieldPerfDropped = z.infer<typeof FieldPerfDroppedSchema>;

const METRIC_SET: ReadonlySet<string> = new Set(PERF_METRICS);
const LABEL_SET: ReadonlySet<string> = new Set(PERF_LABELS);

/**
 * A NAME THIS SERVER DOES NOT KNOW COSTS THAT ONE ENTRY, NOT THE BATCH.
 *
 * The vocabulary is closed so that nothing outside it can be STORED — no worker
 * code as a label, no name in a detail. It used to be enforced by refusing the
 * whole batch with 422, and the device answers a 422 by discarding the batch.
 * So a tablet one release ahead of its server — the normal order, since tablets
 * update themselves — lost every hour that held one new metric name, which was
 * every hour (live, 2026-09-25: four names, the console blank for weeks).
 *
 * Same guarantee, smaller blast radius: an entry whose metric NAME is unknown is
 * skipped; an unknown label or screen becomes '' (unnamed); a detail outside the
 * alphabet becomes ''. Nothing outside the vocabulary is stored, and the rest of
 * the hour is. A MALFORMED entry — a metric that is not a string, a negative
 * count — is still refused below: that is a broken client, not a newer one.
 */
export function tolerateUnknownNames(raw: unknown): unknown {
  if (!Array.isArray(raw)) return raw;
  const out: unknown[] = [];
  for (const e of raw) {
    if (e === null || typeof e !== 'object') {
      out.push(e);
      continue;
    }
    const o = { ...(e as Record<string, unknown>) };
    if (typeof o.m === 'string' && !METRIC_SET.has(o.m)) continue;
    if (typeof o.l === 'string' && !LABEL_SET.has(o.l)) o.l = '';
    if (typeof o.on === 'string' && !LABEL_SET.has(o.on)) o.on = '';
    if (typeof o.d === 'string' && !PERF_DETAIL_RE.test(o.d)) o.d = '';
    out.push(o);
  }
  return out;
}

export const FieldPerfStatSchema = z.object({
  m: z.enum(PERF_METRICS),
  l: z.enum(PERF_LABELS).default(''),
  n: z.number().int().min(1).max(1_000_000),
  s: z.number().min(0).max(1e12),
  x: Ms,
  b: z.array(z.number().int().min(0).max(1_000_000)).max(PERF_BUCKET_COUNT),
});
export type FieldPerfStat = z.infer<typeof FieldPerfStatSchema>;

export const PERF_MAX_STATS_PER_WINDOW = 48;
export const PERF_MAX_WINDOWS_PER_BATCH = 96;
export const PERF_MAX_WORST = 10;
/** One hour, or a day once the device has compacted an old backlog. */
export const PERF_WINDOW_MS = [3_600_000, 86_400_000] as const;

export const FieldPerfWindowSchema = z.object({
  /** Window start, epoch ms (device clock). */
  start: z.number().int().positive(),
  ms: z.union([z.literal(PERF_WINDOW_MS[0]), z.literal(PERF_WINDOW_MS[1])]),
  /** Ms the app was on screen inside this window — the denominator for a rate. */
  activeMs: z.number().int().min(0).max(PERF_WINDOW_MS[1]),
  /** Lowest available RAM sampled inside the window, MB, if any sample. */
  ramMinMb: z.number().int().min(0).max(1_000_000).optional(),
  stats: z.preprocess(tolerateUnknownNames, z.array(FieldPerfStatSchema).max(PERF_MAX_STATS_PER_WINDOW)),
});
export type FieldPerfWindow = z.infer<typeof FieldPerfWindowSchema>;

export const FieldPerfWorstSchema = z.object({
  m: z.enum(PERF_METRICS),
  l: z.enum(PERF_LABELS).default(''),
  ms: Ms,
  at: z.number().int().positive(),
  /** Sanitised context: `got=640x480@30 focus=continuous`. */
  d: z.string().regex(PERF_DETAIL_RE).default(''),
  /** Screen on show at the time — a screen id, never a worker. */
  on: z.enum(PERF_LABELS).default(''),
  /** Available RAM at the last sample before this event, MB. */
  ramMb: z.number().int().min(0).max(1_000_000).optional(),
});
export type FieldPerfWorst = z.infer<typeof FieldPerfWorstSchema>;

export const FieldPerfDeviceFactsSchema = z.object({
  appVersion: z.string().regex(/^[0-9A-Za-z.+-]{1,32}$/).optional(),
  webVersion: z.string().regex(/^[0-9A-Za-z.+-]{1,32}$/).optional(),
  model: z.string().regex(/^[\w .+()/-]{1,64}$/).optional(),
  androidRelease: z.string().regex(/^[0-9A-Za-z.]{1,16}$/).optional(),
  androidSdk: z.number().int().min(1).max(200).optional(),
  webViewMajor: z.number().int().min(1).max(1000).optional(),
  ramTotalMb: z.number().int().min(0).max(1_000_000).optional(),
  ramAvailMb: z.number().int().min(0).max(1_000_000).optional(),
  lowMemory: z.boolean().optional(),
  storageFreeMb: z.number().int().min(0).max(100_000_000).optional(),
  frameSource: z.enum(PERF_FRAME_SOURCES).optional(),
  /** The negotiated camera stream at the last open: `640x480@30`. */
  camera: z.string().regex(/^\d{1,5}x\d{1,5}(@\d{1,3}(\.\d{1,3})?)?$/).optional(),
});
export type FieldPerfDeviceFacts = z.infer<typeof FieldPerfDeviceFactsSchema>;

export const FieldPerfBatchSchema = z.object({
  schema: z.literal(PERF_SCHEMA_VERSION),
  batchKey: z.string().regex(/^[A-Za-z0-9_-]{8,64}$/),
  device: FieldPerfDeviceFactsSchema,
  windows: z.array(FieldPerfWindowSchema).max(PERF_MAX_WINDOWS_PER_BATCH),
  worst: z.preprocess(tolerateUnknownNames, z.array(FieldPerfWorstSchema).max(PERF_MAX_WORST)),
  /** What the device had to throw away since its last accepted batch — a
   *  dropped window must be visible, not silent, and ATTRIBUTED, because the
   *  four causes want four different things done about them. `by` is optional
   *  for one release only: a tablet on an older build reports totals and no
   *  cause, and the console then says it does not know rather than guessing. */
  dropped: FieldPerfDroppedSchema,
});
export type FieldPerfBatch = z.infer<typeof FieldPerfBatchSchema>;

// ── Console read (GET /api/field-perf/devices) ───────────────────────────────

export const FieldPerfRangeSchema = z.enum(['24h', '7d']);
export type FieldPerfRange = z.infer<typeof FieldPerfRangeSchema>;

export const FieldPerfDevicesQuerySchema = z.object({
  range: FieldPerfRangeSchema.default('24h'),
  worksiteId: z.string().min(1).max(64).optional(),
});
export type FieldPerfDevicesQuery = z.infer<typeof FieldPerfDevicesQuerySchema>;

/** p50/p95 are bucket upper edges (see perfHistogram.ts); `max` is exact. */
export interface FieldPerfFigure {
  n: number;
  p50: number;
  p95: number;
  max: number;
}

export interface FieldPerfScreenFigure extends FieldPerfFigure {
  screen: string;
}

export interface FieldPerfDeviceRow {
  deviceId: string;
  name: string;
  worksiteId: string | null;
  worksiteName: string | null;
  /** Newest accepted batch — "last report". Null: never reported. */
  lastReportAt: string | null;
  /** End of the newest measured window — how current the figures are. */
  measuredUntil: string | null;
  /** Facts from the newest batch (not from the range): a tablet's model and
   *  RAM are what it is now, stated with `lastReportAt` beside them. */
  facts: FieldPerfDeviceFacts | null;
  /** Everything below is over the requested range and null when nothing in
   *  the range measured it. Null is "not measured", never "fast". */
  coldStart: FieldPerfFigure | null;
  /** `boot.webframe`: WebView document start -> first frame. */
  webStart: FieldPerfFigure | null;
  screens: FieldPerfScreenFigure[];
  cameraOpen: FieldPerfFigure | null;
  /** `frame.long` + `task.long` (a device records only one of them). `long` is
   *  every frame over 100 ms; `slow` those over PERF_SLOW_FRAME_MS. */
  frames: {
    slow: number;
    long: number;
    activeMinutes: number;
    /** Slow frames per hour on screen; null when there was no on-screen time. */
    slowPerHour: number | null;
  } | null;
  mainBlocks: { n: number; max: number } | null;
  /** Summed over the batches in the range. `by` is what those batches could
   *  attribute; `unattributed` is what they could not — an older tablet's
   *  totals, which the console states as unknown rather than as a full buffer. */
  dropped: { windows: number; events: number; by: FieldPerfDropBy; unattributed: { windows: number; events: number } };
  worst: FieldPerfWorst[];
  /** Camera faults over the last 7 days WHATEVER the range (perfCamera.ts):
   *  `stuck` opens or first frames over CAMERA_STUCK_MS, `failed` cameras that
   *  never came up. Counted from the windows plus any worst event newer than
   *  them, so the count and the worst-event list cannot disagree. */
  cameraWeek: { stuck: number; failed: number };
}

export interface FieldPerfDevicesResponse {
  range: FieldPerfRange;
  generatedAt: string;
  rows: FieldPerfDeviceRow[];
}
